import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Landmark, RefreshCw, Link2, Upload, ShieldCheck, Lock, AlertCircle, PiggyBank, Wallet,
} from "lucide-react";
import {
    listKonten, proposeKonten, assignKonten, setKontoAbschluss,
    listKautionen, proposeKautionen, assignKautionen, importAuszug,
    setKontoArt, resetKontoArt, listUnits, listProperties, listTenants,
} from "@/lib/api";
import { euro, germanDate } from "@/lib/format";
import { SearchBox, useFulltextFilter, MatchCount } from "@/components/SearchBox";
import SuchAuswahl from "@/components/ui/such-auswahl";
import { toast } from "sonner";
import { useSchreibschutz } from "@/lib/auth";
import { Seitenkopf } from "@/hilfe/Seitenkopf";
import { Grund } from "@/hilfe/Grund";
import { sperre } from "@/hilfe/Knopfhilfe";
import { Hilfe } from "@/hilfe/Hilfe";

// Die Kontoarten des Backends, mit derselben Bedeutung. "unklar" ist kein Fehler,
// sondern die ehrliche Antwort: hier liegen die Entitäts- und Sammelkonten, deren
// Bewegungen sechs- bis siebenstellig sind und die deshalb nichts in der
// Mietzuordnung zu suchen haben.
const ART = {
    miete:    { label: "Miete",    was: "Mieteingang — auf Einheit, Immobilie oder Entität",
                ton: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    kaution:  { label: "Kaution",  was: "Mietkaution",
                ton: "text-sky-700 bg-sky-50 border-sky-200" },
    darlehen: { label: "Darlehen", was: "gehört zur Finanzierung, nicht zum Mietkonto",
                ton: "text-violet-700 bg-violet-50 border-violet-200" },
    unklar:   { label: "Unklar",   was: "noch nicht eingeordnet",
                ton: "text-amber-700 bg-amber-50 border-amber-200" },
    privat:   { label: "Privat",   was: "gehört nicht zur Immobilienverwaltung",
                ton: "text-gray-500 bg-gray-50 border-gray-200" },
};
// Was im Alltag gezeigt wird. Darlehens- und Privatkonten bleiben im Bestand —
// sie kommen bei jedem Import wieder —, stehen aber nicht in der Liste, aus der
// man Mieten zuordnet. Über den Filter sind sie jederzeit erreichbar.
const ARBEITSARTEN = ["miete", "kaution", "unklar"];

// Zustand einer Kaution, abgeleitet vom Backend. Nicht hier nachrechnen — sonst
// laufen Oberfläche und Server auseinander.
const ZUSTAND = {
    aktiv:              { label: "aktiv",              ton: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    abgerechnet:        { label: "abgerechnet",        ton: "text-gray-600 bg-gray-50 border-gray-200" },
    offen_nach_auszug:  { label: "offen nach Auszug",  ton: "text-rose-700 bg-rose-50 border-rose-200" },
    unklar:             { label: "unklar",             ton: "text-amber-700 bg-amber-50 border-amber-200" },
};

const ABSCHLUSS = [
    { value: "abgeschlossen", label: "Abgeschlossen — nichts mehr zu tun" },
    { value: "rueckbehalt",   label: "Rückbehalt — Geld wird bis zu einem Anlass gehalten", anlass: true },
];
const ABSCHLUSS_BY = Object.fromEntries(ABSCHLUSS.map((a) => [a.value, a]));

// Suchfelder auf Modulebene: als Literal im Rendern wuerden sie bei jedem Lauf
// neu entstehen und das useMemo im Filter jedes Mal verwerfen.
const KONTO_FELDER = ["bezeichnung", "iban", "art", (k) => k.zuordnung?.bezeichnung];
const KAUTION_FELDER = ["account_bezeichnung", "mieter", "einheit", "zustand"];

const Merkmal = ({ text, ton }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] font-medium ${ton}`}>
        {text}
    </span>
);

const Kachel = ({ label, wert, ton = "neutral", fuss, testid }) => {
    const farbe = ton === "rot" ? "text-rose-600" : ton === "gelb" ? "text-amber-600"
        : ton === "gruen" ? "text-emerald-600" : "text-slate-900";
    return (
        <div className="bg-white border border-gray-200 rounded-md p-4" data-testid={testid}>
            {/* Beschriftung und Betrag duerfen die Kachel nicht sprengen: bei
                1100 px Fensterbreite stand "24.984,00 EUR" 33 px daneben. Die
                Schriftgroesse richtet sich deshalb nach der Laenge. */}
            <div className="kachel-label uppercase text-gray-500" title={label}>{label}</div>
            <div className={`mt-2 font-bold tabular-nums break-words ${farbe}
                             ${String(wert).length > 9 ? "text-lg" : String(wert).length > 6 ? "text-xl" : "text-2xl"}`}>
                {wert}
            </div>
            {fuss && <div className="mt-1 text-[11px] text-gray-400 leading-snug">{fuss}</div>}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Vermerk setzen — abgeschlossen oder Rückbehalt
// ---------------------------------------------------------------------------
const AbschlussDialog = ({ konto, open, onOpenChange, onSaved }) => {
    const [status, setStatus] = useState("abgeschlossen");
    const [grund, setGrund] = useState("");
    const [anlass, setAnlass] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        const a = konto?.abschluss;
        setStatus(a?.status || "abgeschlossen");
        setGrund(a?.grund || "");
        setAnlass(a?.anlass || "");
    }, [open, konto]);

    const def = ABSCHLUSS_BY[status] || {};

    const speichern = async () => {
        if (!grund.trim()) { toast.error("Ein Grund ist Pflicht."); return; }
        if (def.anlass && !anlass.trim()) {
            toast.error("Ein Rückbehalt braucht einen Anlass.", {
                description: "Sonst ist er von einem vergessenen Restsaldo nicht zu unterscheiden.",
            });
            return;
        }
        setSaving(true);
        try {
            await setKontoAbschluss(konto.iban, {
                status, grund: grund.trim(), anlass: def.anlass ? anlass.trim() : null,
            });
            toast.success(status === "rueckbehalt" ? "Als Rückbehalt vermerkt" : "Als abgeschlossen vermerkt");
            onSaved();
            onOpenChange(false);
        } finally { setSaving(false); }
    };

    const entfernen = async () => {
        setSaving(true);
        try {
            await setKontoAbschluss(konto.iban, null);
            toast.success("Vermerk entfernt — das Konto ist wieder ein offener Fund");
            onSaved();
            onOpenChange(false);
        } finally { setSaving(false); }
    };

    if (!konto) return null;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Konto vermerken</DialogTitle>
                    <DialogDescription>
                        {konto.bezeichnung} · Saldo {euro(konto.saldo)}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <div>
                        <Label>Status</Label>
                        <SuchAuswahl wert={status} onWaehlen={setStatus}
                            optionen={[
                                ...ABSCHLUSS.map((a) => ({ wert: a.value, text: a.label })),
                            ]}
                            testId="abschluss-status"
                        />
                        <div className="mt-1 text-[11px] text-gray-500">
                            {status === "rueckbehalt"
                                ? "Bleibt sichtbar, mit Betrag und Anlass — absichtlich gehaltenes Geld ist eine Aufgabe."
                                : "Verschwindet aus der Arbeitsliste. Der Saldo bleibt unangetastet."}
                        </div>
                    </div>

                    <div>
                        <Label>Grund <span className="text-rose-600">*</span></Label>
                        <Input value={grund} onChange={(e) => setGrund(e.target.value)}
                               placeholder="z.B. Einheit verkauft, keine Immobilie mehr in der App"
                               data-testid="abschluss-grund" />
                    </div>

                    {def.anlass && (
                        <div>
                            <Label>Anlass <span className="text-rose-600">*</span></Label>
                            <Input value={anlass} onChange={(e) => setAnlass(e.target.value)}
                                   placeholder="z.B. NK-Abrechnung 2025"
                                   data-testid="abschluss-anlass" />
                            <div className="mt-1 text-[11px] text-gray-500">
                                Worauf gewartet wird. Ohne Anlass wäre der Rückbehalt von einem
                                vergessenen Restsaldo nicht zu unterscheiden.
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter className="gap-2">
                    {konto.abschluss && (
                        <Button variant="outline" onClick={entfernen} disabled={saving}
                                grund={sperre([saving, "Wird gespeichert"])}
                                data-testid="abschluss-entfernen">
                            Vermerk entfernen
                        </Button>
                    )}
                    <Button onClick={speichern} disabled={saving}
                            grund={sperre([saving, "Wird gespeichert"])}
                            className="bg-slate-900 hover:bg-slate-800" data-testid="abschluss-speichern">
                        Speichern
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// Kontoauszug einlesen
// ---------------------------------------------------------------------------
const ImportDialog = ({ open, onOpenChange, onDone }) => {
    const [datei, setDatei] = useState(null);
    const [vorschau, setVorschau] = useState(null);
    const [fehler, setFehler] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) { setDatei(null); setVorschau(null); setFehler(null); }
    }, [open]);

    // Warum der Fehler hier landen MUSS: ohne catch blieb vorschau null, der
    // Knopf deaktiviert und beschriftet mit "Erst Datei wählen" -- obwohl die
    // Datei gewählt war. Der Interceptor in api.js behandelt 401/403/409, ein
    // 400 oder 422 lief also stumm ins Leere. Am 03.10.2026 war so eine Stunde
    // nicht nachvollziehbar, warum nichts passierte.
    const fehlertext = (e) => {
        const d = e?.response?.data?.detail;
        if (typeof d === "string") return d;
        if (d?.error) return [d.error, d.detail].filter(Boolean).join(" — ");
        if (Array.isArray(d)) return "Die Datei kam nicht richtig beim Server an.";
        if (e?.code === "ECONNABORTED") return "Zeitüberschreitung — Datei zu groß?";
        return e?.message || "Unbekannter Fehler";
    };

    const pruefen = async (f) => {
        setDatei(f); setVorschau(null); setFehler(null);
        if (!f) return;
        setBusy(true);
        try { setVorschau(await importAuszug(f, false)); }
        catch (e) { setFehler(fehlertext(e)); }
        finally { setBusy(false); }
    };

    const schreiben = async () => {
        setBusy(true);
        try {
            const r = await importAuszug(datei, true);
            toast.success(`${r.davon_geschrieben} Buchungen eingelesen`, {
                description: `${r.davon_bereits_vorhanden} waren schon da und wurden nicht doppelt angelegt.`,
            });
            onDone();
            onOpenChange(false);
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Kontoauszug einlesen</DialogTitle>
                    <DialogDescription>
                        Banking4-Tabellenexport. Wird zuerst nur geprüft. Derselbe Export zweimal
                        eingelesen legt nichts doppelt an — auch überlappende Zeiträume nicht.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <div>
                        <Label>Datei<Hilfe id="banking4-export" /></Label>
                        <Input type="file" accept=".csv,.txt"
                               onChange={(e) => pruefen(e.target.files?.[0] || null)}
                               data-testid="import-datei" />
                    </div>

                    {busy && <div className="py-4 text-center text-sm text-gray-400">Wird geprüft …</div>}

                    {fehler && !busy && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800"
                             data-testid="import-fehler">
                            <b>Nicht eingelesen.</b> {fehler}
                        </div>
                    )}

                    {vorschau && (
                        <div className="space-y-1.5 text-sm">
                            <div className="flex justify-between"><span className="text-gray-500">Format</span>
                                <b>{vorschau.format} · {vorschau.kodierung}</b></div>
                            <div className="flex justify-between"><span className="text-gray-500">Zeilen gelesen<Hilfe id="zeilen-gelesen" /></span>
                                <b className="tabular-nums">{vorschau.zeilen_gelesen}</b></div>
                            <div className="flex justify-between"><span className="text-gray-500">Buchungen erkannt<Hilfe id="buchungen-erkannt" /></span>
                                <b className="tabular-nums">{vorschau.zahlungen_erkannt}</b></div>
                            <div className="flex justify-between"><span className="text-gray-500">Schon vorhanden<Hilfe id="schon-vorhanden" /></span>
                                <b className="tabular-nums">{vorschau.davon_bereits_vorhanden}</b></div>
                            <div className="flex justify-between"><span className="text-gray-500">Konten in der Datei<Hilfe id="konten-im-auszug" /></span>
                                <b className="tabular-nums">{vorschau.konten?.length ?? 0}</b></div>
                            {vorschau.zeitraum && (
                                <div className="flex justify-between"><span className="text-gray-500">Zeitraum</span>
                                    <b>{germanDate(vorschau.zeitraum.von)} – {germanDate(vorschau.zeitraum.bis)}</b></div>
                            )}
                            {vorschau.uebersprungen_anzahl > 0 && (
                                <div className="mt-2 bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800">
                                    {vorschau.uebersprungen_anzahl} Zeilen übersprungen — meist Hinweisbuchungen
                                    über 0,00 €.
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button onClick={schreiben} disabled={busy || !vorschau?.zahlungen_erkannt}
                            grund={sperre([busy, "Wird ausgeführt"], [!datei, "Keine Datei gewählt"],
                                          [!vorschau, "Datei nicht verwendbar"],
                                          [!vorschau?.zahlungen_erkannt, "Keine Buchungen erkannt"])}
                            className="bg-slate-900 hover:bg-slate-800" data-testid="import-schreiben">
                        {vorschau ? "Einlesen" : (datei ? "Datei nicht verwendbar" : "Erst Datei wählen")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// Zuordnungsvorschlag für Konten bzw. Kautionen
// ---------------------------------------------------------------------------
// Bewusst ein Dialog für beide Fälle: das Verfahren ist dasselbe (Adresse, dann
// Lage aus der Kontobezeichnung), nur das Ziel unterscheidet sich. Zwei fast
// gleiche Dialoge würden auseinanderlaufen.
const VorschlagDialog = ({ art, open, onOpenChange, onDone }) => {
    const [vorschau, setVorschau] = useState(null);
    const [busy, setBusy] = useState(false);
    // Was der Nutzer am Vorschlag geändert hat, je Konto-IBAN. Der Vorschlag
    // selbst bleibt unangetastet — sonst weiß man hinterher nicht mehr, was
    // abgeleitet war und was von Hand kam.
    const [korrektur, setKorrektur] = useState({});
    const [stamm, setStamm] = useState({ units: [], props: [], tenants: [] });
    const kaution = art === "kaution";

    useEffect(() => {
        if (!open) { setVorschau(null); setKorrektur({}); return; }
        setBusy(true);
        Promise.all([
            kaution ? proposeKautionen() : proposeKonten(),
            kaution ? listUnits() : Promise.resolve([]),
            kaution ? listProperties() : Promise.resolve([]),
            kaution ? listTenants() : Promise.resolve([]),
        ]).then(([v, units, props, tenants]) => {
            setVorschau(v);
            setStamm({ units, props, tenants });
        }).finally(() => setBusy(false));
    }, [open, kaution]);

    // Der geltende Wert: Korrektur, sonst Vorschlag.
    const wert = (v, feld) => {
        const k = korrektur[v.account_iban || v.iban];
        return k && feld in k ? k[feld] : v[feld];
    };
    const setzen = (v, feld, neu) => setKorrektur((alt) => {
        const key = v.account_iban || v.iban;
        return { ...alt, [key]: { ...(alt[key] || {}), [feld]: neu } };
    });

    const schreiben = async () => {
        setBusy(true);
        try {
            const v = vorschau.vorschlaege;
            if (kaution) {
                const k = (x, f) => {
                    const c = korrektur[x.account_iban];
                    return c && f in c ? c[f] : x[f];
                };
                const geaendert = Object.keys(korrektur).length;
                const r = await assignKautionen(v.map((x) => ({
                    account_iban: x.account_iban,
                    unit_id: k(x, "unit_id") || null,
                    property_id: k(x, "property_id") || null,
                    tenant_id: k(x, "tenant_id") || null,
                    notiz: korrektur[x.account_iban]
                        ? "von Hand zugeordnet"
                        : ([x.begruendung_mieter, x.hinweis].filter(Boolean).join(" · ") || null),
                })), { apply: true });
                toast.success(`${r.erfasst} Kautionen erfasst`, {
                    description: geaendert
                        ? `${geaendert} davon von Hand geändert.`
                        : undefined,
                });
            } else {
                // force nur, wenn ein angehaktes Hauptkonto wirklich etwas
                // ersetzt. Pauschal mitzuschicken hiesse, auch die Sperre an
                // Einheiten und Immobilien stillzulegen.
                const ersetzt = v.some((x) => x.ebene === "entity" && wert(x, "hauptkonto")
                    && x.hauptkonto_bisher && !x.hauptkonto_ist_gesetzt);
                // Nur die offenen schicken: die erledigten kämen ohnehin als
                // „war schon gesetzt" zurück und blähten die Meldung auf.
                const zuTun = v.filter(istOffen);
                const r = await assignKonten(zuTun.map((x) => ({
                    iban: x.iban, ebene: x.ebene, ziel_id: x.ziel_id,
                    bezeichnung: x.ziel_bezeichnung,
                    hauptkonto: x.ebene === "entity" ? !!wert(x, "hauptkonto") : false,
                })), { apply: true, force: ersetzt });
                // Die Gruende kommen aus der Antwort, nicht aus einem festen
                // Satz. Der feste Satz behauptete "dort stand schon eine andere
                // IBAN" — und stand am 09.09. auch dann da, wenn in Wahrheit
                // alles längst zugeordnet war. Eine Meldung, die einen Grund
                // erfindet, schickt einen auf die Suche nach einem Fehler, den
                // es nicht gibt.
                const gruende = {};
                (r.uebersprungen || []).forEach((u) => {
                    const g = u.grund || "ohne Angabe";
                    gruende[g] = (gruende[g] || 0) + 1;
                });
                const text = Object.entries(gruende)
                    .sort((a, b) => b[1] - a[1])
                    .map(([g, n]) => `${n}× ${g}`)
                    .join(" · ");
                toast.success(`${r.zugeordnet} Konten zugeordnet`, {
                    description: text || undefined,
                });
            }
            onDone();
            onOpenChange(false);
        } finally { setBusy(false); }
    };

    const anzahl = vorschau?.vorschlaege?.length ?? 0;
    // Was der Knopf wirklich tut. Bis zum 10.09.2026 stand dort die Anzahl
    // ALLER Vorschläge — auch derer, die längst zugeordnet waren. Man drückte
    // „21 übernehmen", es passierte nichts, und die Liste sah aus, als wäre
    // sie kaputt. Gezählt wird jetzt, was sich ändern würde.
    const istOffen = (v) => !v.bereits_gesetzt
        || (v.ebene === "entity" && wert(v, "hauptkonto") && !v.hauptkonto_ist_gesetzt);
    const offene = (vorschau?.vorschlaege || []).filter(istOffen);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{kaution ? "Kautionen zuordnen" : "Konten zuordnen"}</DialogTitle>
                    <DialogDescription>
                        Abgeleitet aus Adresse und Hausnummer der Kontobezeichnung, bei mehreren
                        Treffern entscheidet die Lage. Nennt die Bezeichnung statt einer Adresse
                        den Namen einer Entität, gilt das Konto als deren Sammelkonto.
                        Wird zuerst nur vorgeschlagen.
                    </DialogDescription>
                </DialogHeader>

                {!vorschau ? (
                    <div className="py-8 text-center text-sm text-gray-400">Wird geprüft …</div>
                ) : (
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-gray-500">Konten im Bestand</span>
                            <b className="tabular-nums">{vorschau.konten}</b></div>
                        {!kaution && (
                            <div className="flex justify-between"><span className="text-gray-500">Davon Mietkonten</span>
                                <b className="tabular-nums">{vorschau.davon_mietkonten}</b></div>
                        )}
                        <div className="flex justify-between"><span className="text-gray-500">Vorschläge</span>
                            <b className="tabular-nums">{anzahl}</b></div>
                        {kaution ? (
                            <>
                                <div className="flex justify-between"><span className="text-gray-500">Davon mit Einheit</span>
                                    <b className="tabular-nums">{vorschau.davon_mit_einheit}</b></div>
                                <div className="flex justify-between"><span className="text-gray-500">Davon mit Mieter</span>
                                    <b className="tabular-nums">{vorschau.davon_mit_mieter}</b></div>
                            </>
                        ) : (
                            <>
                                <div className="flex justify-between"><span className="text-gray-500">Auf eine Einheit</span>
                                    <b className="tabular-nums">{vorschau.davon_auf_einheit}</b></div>
                                <div className="flex justify-between"><span className="text-gray-500">Auf eine Immobilie</span>
                                    <b className="tabular-nums">{vorschau.davon_auf_immobilie}</b></div>
                                {/* Sammelkonten: kein Objekt, sondern der Eigentümer. Dort wird
                                    keine IBAN in die Stammdaten geschrieben — eine Entität hat
                                    mehrere Konten, das Feld dort hätte nur für eines Platz. */}
                                <div className="flex justify-between"><span className="text-gray-500">Auf eine Entität (Sammelkonto)</span>
                                    <b className="tabular-nums">{vorschau.davon_auf_entitaet ?? 0}</b></div>
                                {vorschau.davon_mit_konflikt > 0 && (
                                    <div className="flex justify-between text-amber-700">
                                        <span>Mit Konflikt (dort steht schon eine andere IBAN)</span>
                                        <b className="tabular-nums">{vorschau.davon_mit_konflikt}</b></div>
                                )}
                            </>
                        )}

                        {anzahl > 0 && (
                            <div className="mt-3 max-h-[26rem] overflow-y-auto border border-gray-100 rounded-md">
                                <table className="w-full text-xs">
                                    <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-400 sticky top-0">
                                        <tr>
                                            <th className="text-left px-2 py-1.5">Konto</th>
                                            <th className="text-right px-2 py-1.5">Saldo</th>
                                            <th className="text-left px-2 py-1.5">{kaution ? "Einheit" : "Ziel"}</th>
                                            <th className="text-left px-2 py-1.5">{kaution ? "Mieter" : "Ebene"}</th>
                                            {!kaution && <th className="text-left px-2 py-1.5">Stand</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                    {vorschau.vorschlaege.map((v, i) => {
                                        const key = v.account_iban || v.iban;
                                        const geaendert = !!korrektur[key];
                                        return (
                                        <tr key={i} className={`border-b border-gray-50 last:border-0 ${geaendert ? "bg-amber-50/60" : ""}`}>
                                            <td className="px-2 py-1.5">
                                                {v.bezeichnung || v.kontobezeichnung}
                                                {geaendert && (
                                                    <span className="ml-1.5 text-[9px] text-amber-700">geändert</span>
                                                )}
                                            </td>
                                            <td className="px-2 py-1.5 tabular-nums text-right w-24">{euro(v.saldo)}</td>
                                            {/* Bei Kautionen änderbar: die Kontobezeichnung nennt oft die
                                                Lage ("1.OG"), aber die Einheiten der Familienstiftung heissen
                                                nur "WE 01" — dort kann die Ableitung nichts treffen, und ohne
                                                Handzuordnung bliebe die Kaution fuer immer offen. */}
                                            {kaution ? (
                                                <>
                                                    <td className="px-2 py-1.5">
                                                        <select
                                                            className="w-full max-w-[190px] border border-gray-200 rounded px-1 py-0.5 text-xs bg-white"
                                                            value={wert(v, "unit_id") || ""}
                                                            data-testid={`kaution-einheit-${key}`}
                                                            onChange={(e) => setzen(v, "unit_id", e.target.value || null)}>
                                                            <option value="">— keine Einheit —</option>
                                                            {stamm.units
                                                                .filter((u) => !wert(v, "property_id")
                                                                    || u.property_id === wert(v, "property_id"))
                                                                .map((u) => (
                                                                    <option key={u.id} value={u.id}>{u.unit_label}</option>
                                                                ))}
                                                        </select>
                                                    </td>
                                                    <td className="px-2 py-1.5">
                                                        <select
                                                            className="w-full max-w-[170px] border border-gray-200 rounded px-1 py-0.5 text-xs bg-white"
                                                            value={wert(v, "tenant_id") || ""}
                                                            data-testid={`kaution-mieter-${key}`}
                                                            onChange={(e) => setzen(v, "tenant_id", e.target.value || null)}>
                                                            <option value="">— kein Mieter —</option>
                                                            {stamm.tenants
                                                                .filter((t) => !wert(v, "unit_id") || t.unit_id === wert(v, "unit_id"))
                                                                .map((t) => (
                                                                    <option key={t.id} value={t.id}>
                                                                        {t.full_name}{t.status !== "aktiv" ? " (früher)" : ""}
                                                                    </option>
                                                                ))}
                                                        </select>
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td className="px-2 py-1.5 text-gray-600">{v.ziel_bezeichnung || "—"}</td>
                                                    <td className="px-2 py-1.5 text-gray-400">
                                                        {v.ebene}
                                                        {/* Nur bei Sammelkonten: welches der Konten einer
                                                            Entität ist ihr Hauptkonto? Genau dieses eine
                                                            landet als IBAN in ihren Stammdaten. */}
                                                        {v.ebene === "entity" && (
                                                            <label className="ml-2 inline-flex items-center gap-1 text-gray-600">
                                                                <input
                                                                    type="checkbox"
                                                                    className="h-3 w-3"
                                                                    checked={!!wert(v, "hauptkonto")}
                                                                    data-testid={`hauptkonto-${key}`}
                                                                    onChange={(e) => setzen(v, "hauptkonto", e.target.checked)} />
                                                                Hauptkonto
                                                            </label>
                                                        )}
                                                        {v.ebene === "entity" && v.hauptkonto_bisher
                                                            && !v.hauptkonto_ist_gesetzt && wert(v, "hauptkonto") && (
                                                            <div className="text-[10px] text-amber-700">
                                                                ersetzt {v.hauptkonto_bisher.slice(0, 12)}…
                                                            </div>
                                                        )}
                                                    </td>
                                                    {/* Ohne diese Spalte sieht eine erledigte Zeile aus
                                                        wie eine offene — und der Knopf scheint nichts zu tun. */}
                                                    <td className="px-2 py-1.5 text-[11px] w-28">
                                                        {v.konflikt ? (
                                                            <span className="text-amber-700">Konflikt</span>
                                                        ) : istOffen(v) ? (
                                                            <span className="text-slate-900 font-medium">offen</span>
                                                        ) : (
                                                            <span className="text-gray-400">schon gesetzt</span>
                                                        )}
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {kaution && (
                            <div className="text-[11px] text-gray-500 leading-relaxed">
                                Einheit und Mieter sind änderbar. Die Ableitung liest die Lage aus der
                                Kontobezeichnung — bei den Objekten der Familienstiftung heissen die
                                Einheiten aber nur „WE 01" bis „WE 04", ohne Etage. Dort kann sie nichts
                                treffen, und die Zuordnung bleibt Handarbeit, bis die Etage im Namen der
                                Einheit steht.
                            </div>
                        )}

                        {vorschau.ohne_vorschlag?.length > 0 && (
                            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-md p-3 text-xs">
                                <b className="text-amber-900">
                                    {vorschau.ohne_vorschlag.length} Konten ohne Vorschlag
                                </b>
                                <div className="text-amber-800 mt-1 space-y-0.5">
                                    {vorschau.ohne_vorschlag.slice(0, 8).map((o, i) => (
                                        <div key={i}>{o.bezeichnung} — {o.grund}</div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {vorschau.andere_kontoarten?.length > 0 && (
                            <div className="mt-2 bg-gray-50 border border-gray-200 rounded-md p-3 text-xs text-gray-600">
                                {vorschau.andere_kontoarten.length} Konten sind ausgenommen, weil sie
                                keine Mietkonten sind — die IBAN an einer Einheit bedeutet „hierher
                                kommt die Miete". Kautionen laufen über den eigenen Tab.
                            </div>
                        )}

                        {vorschau.vermerkt?.length > 0 && (
                            <div className="mt-2 bg-gray-50 border border-gray-200 rounded-md p-3 text-xs text-gray-600">
                                {vorschau.vermerkt.length} vermerkte Konten sind hier ausgenommen
                                {vorschau.summe_rueckbehalt > 0 && (
                                    <>, davon {euro(vorschau.summe_rueckbehalt)} Rückbehalt</>
                                )}.
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button onClick={schreiben}
                            disabled={busy || (kaution ? !anzahl : offene.length === 0)}
                            grund={sperre([busy, "Wird ausgeführt"], [!anzahl, "Nichts vorzuschlagen"],
                                          [offene.length === 0, "Alles bereits zugeordnet"])}
                            className="bg-slate-900 hover:bg-slate-800" data-testid="vorschlag-schreiben">
                        {kaution
                            ? (anzahl ? `${anzahl} übernehmen` : "Nichts vorzuschlagen")
                            : (offene.length
                                ? `${offene.length} übernehmen`
                                : anzahl ? "Alles bereits zugeordnet" : "Nichts vorzuschlagen")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// Seite
// ---------------------------------------------------------------------------
const Konten = () => {
    const [konten, setKonten] = useState([]);
    const [kautionen, setKautionen] = useState(null);
    const [busy, setBusy] = useState(false);
    const [importOffen, setImportOffen] = useState(false);
    const [vorschlagArt, setVorschlagArt] = useState(null);
    const [abschlussKonto, setAbschlussKonto] = useState(null);
    const [artFilter, setArtFilter] = useState("arbeit");
    const [tab, setTab] = useState("konten");
    // Sperren, BEVOR geklickt wird. Erst beim Speichern eine 403 zu zeigen ist
    // eine schlechte Auskunft: der Nutzer erfaehrt dann nach getaner Arbeit,
    // dass er sie nicht tun durfte.
    const { gesperrt, grund } = useSchreibschutz();

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const [k, kt] = await Promise.all([listKonten(), listKautionen()]);
            setKonten(k);
            setKautionen(kt);
        } finally { setBusy(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const nachArt = useMemo(() => {
        const z = {};
        konten.forEach((k) => { z[k.art] = (z[k.art] || 0) + 1; });
        return z;
    }, [konten]);

    const sichtbar = useMemo(() => {
        if (artFilter === "alle") return konten;
        if (artFilter === "arbeit") return konten.filter((k) => ARBEITSARTEN.includes(k.art));
        return konten.filter((k) => k.art === artFilter);
    }, [konten, artFilter]);

    // Art von Hand setzen. Die Erkennung liest das Präfix der Kontobezeichnung
    // und kann prinzipiell nicht wissen, dass ein „Girokonto" privat ist oder
    // dass auf dem Konto einer Entität Mieten eingehen — das steht nirgends in
    // den Daten. Deshalb muss man sie überstimmen können.
    const artAendern = async (k, art) => {
        try {
            if (art === "__erkennung") {
                await resetKontoArt(k.iban);
                toast.success(`${k.bezeichnung}: Handkorrektur zurückgenommen`, {
                    description: `Es gilt wieder die Erkennung: ${ART[k.art_erkannt]?.label || k.art_erkannt}.`,
                });
            } else {
                await setKontoArt(k.iban, art);
                toast.success(`${k.bezeichnung} ist jetzt „${ART[art]?.label || art}"`, {
                    description: art === "privat"
                        ? "Verschwindet aus der Arbeitsliste, bleibt aber im Bestand."
                        : undefined,
                });
            }
            load();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error("Nicht geändert", { description: typeof d === "string" ? d : undefined });
        }
    };
    const { query, setQuery, filtered } = useFulltextFilter(sichtbar, KONTO_FELDER);

    const kautionsListe = kautionen?.kautionen ?? [];
    const { query: kq, setQuery: setKq, filtered: kFiltered } =
        useFulltextFilter(kautionsListe, KAUTION_FELDER);

    const rueckbehalte = konten.filter((k) => k.abschluss?.status === "rueckbehalt");
    const ohneZuordnung = konten.filter(
        (k) => k.art === "miete" && !k.zuordnung && !k.abschluss);

    return (
        <div className="px-6 md:px-10 py-8 space-y-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">Betrieb</div>
                    <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="konten-title">
                        Bankkonten
                    </h1>
                    <p className="text-sm text-gray-500 mt-1 max-w-xl">
                        Die eingelesenen Konten und die Kautionen, die darauf liegen. Ohne die
                        Zuordnung Konto → Einheit kann keine Zahlung automatisch verteilt werden.
                    </p>
                    <Seitenkopf id="seite.konten" />
                </div>
                <div className="flex items-end gap-2">
                    <Button variant="outline" size="sm" onClick={load} disabled={busy} className="h-9"
                            grund={sperre([busy, "Wird geladen"])}>
                        <RefreshCw size={14} className={busy ? "animate-spin" : ""} />
                    </Button>
                    <Button size="sm" variant="outline" className="h-9"
                            onClick={() => setImportOffen(true)} data-testid="btn-import"
                            disabled={gesperrt} grund={grund}>
                        <Upload size={14} className="mr-1.5" /> Auszug einlesen
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Kachel label="Konten" wert={konten.length} testid="kachel-konten"
                        fuss={`${nachArt.miete || 0} Miete · ${nachArt.kaution || 0} Kaution`} />
                <Kachel label="Ohne Zuordnung" wert={ohneZuordnung.length}
                        ton={ohneZuordnung.length ? "rot" : "gruen"} testid="kachel-ohne-zuordnung"
                        fuss="Mietkonten ohne Einheit — dort verteilt nichts automatisch" />
                <Kachel label="Kautionen" wert={kautionen?.anzahl ?? 0} testid="kachel-kautionen"
                        fuss={kautionen ? euro(kautionen.summe_gesamt) : null} />
                <Kachel label="Aktiv gehalten"
                        wert={euro(kautionen?.summe_je_zustand?.aktiv ?? 0)} testid="kachel-aktiv"
                        fuss="Kautionen laufender Mietverhältnisse" />
                <Kachel label="Rückbehalt" wert={euro(rueckbehalte.reduce((s, k) => s + (k.saldo || 0), 0))}
                        ton={rueckbehalte.length ? "gelb" : "neutral"} testid="kachel-rueckbehalt"
                        fuss={rueckbehalte.length ? `${rueckbehalte.length} Konto/Konten mit Anlass` : "keiner"} />
            </div>

            {rueckbehalte.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-4" data-testid="rueckbehalt-banner">
                    <div className="flex items-start gap-2">
                        <Lock size={16} className="text-amber-700 mt-0.5 shrink-0" />
                        <div className="text-sm">
                            <b className="text-amber-900">Absichtlich gehaltenes Geld</b>
                            <div className="text-amber-800 mt-1 space-y-0.5">
                                {rueckbehalte.map((k) => (
                                    <div key={k.iban}>
                                        {k.bezeichnung} — <b className="tabular-nums">{euro(k.saldo)}</b>,
                                        wartet auf: {k.abschluss.anlass}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Der Reiter ist der Filter: „Mieten" ist die Liste, aus der
                Mieteingänge zugeordnet werden, und die braucht man am
                häufigsten. Über die Auswahl daneben bleibt jede andere Art
                erreichbar — der Reiter stellt sie nur voreingestellt ein. */}
            <Tabs value={tab} onValueChange={(v) => {
                setTab(v);
                if (v === "mieten") setArtFilter("miete");
                if (v === "konten") setArtFilter("arbeit");
            }}>
                <TabsList>
                    <TabsTrigger value="konten" data-testid="tab-konten">
                        <Landmark size={14} className="mr-1.5" /> Konten
                    </TabsTrigger>
                    <TabsTrigger value="mieten" data-testid="tab-mieten">
                        <Wallet size={14} className="mr-1.5" /> Mieten ({nachArt.miete || 0})
                    </TabsTrigger>
                    <TabsTrigger value="kautionen" data-testid="tab-kautionen">
                        <PiggyBank size={14} className="mr-1.5" /> Kautionen
                    </TabsTrigger>
                </TabsList>

                <TabsContent value={tab === "kautionen" ? "__aus" : tab}
                             className="mt-4 space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <SearchBox value={query} onChange={setQuery} testid="konten-suche"
                                   placeholder="Bezeichnung, IBAN, Ziel …" />
                        <SuchAuswahl wert={artFilter} onWaehlen={setArtFilter}
                            optionen={[
                                { wert: "arbeit", text: `Arbeitsliste (${ARBEITSARTEN.reduce((n, a) => n + (nachArt[a] || 0), 0)})` },
                                { wert: "alle", text: `Alle Arten (${konten.length})` },
                                ...Object.entries(ART).map(([k, v]) => ({ wert: k, text: `${v.label} (${nachArt[k] || 0})` })),
                            ]}
                            className="w-[160px]"
                            testId="konten-art"
                        />
                        <MatchCount matches={filtered.length} total={konten.length} label="Konten" />
                        <div className="ml-auto">
                            <Button size="sm" className="bg-slate-900 hover:bg-slate-800 h-9"
                                    onClick={() => setVorschlagArt("miete")} data-testid="btn-konten-zuordnen"
                                    disabled={gesperrt} grund={grund}>
                                <Link2 size={14} className="mr-1.5" /> Konten zuordnen
                            </Button>
                        </div>
                    </div>

                    {/* overflow-x-auto, nicht overflow-hidden: die Tabelle ist breiter als der
                        Bildschirm, sobald genug Spalten da sind — mit "hidden" verschwinden die
                        rechten Spalten lautlos und sind auf keinem Weg erreichbar. Gemessen am
                        10.09.2026 bei 1280 px: Mieter 210 px abgeschnitten, Einheiten 183 px. */}
                    <div className="bg-white border border-gray-200 rounded-md overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                                <tr>
                                    <th className="text-left px-4 py-2.5">Bezeichnung</th>
                                    <th className="text-left px-4 py-2.5">Art</th>
                                    <th className="text-right px-4 py-2.5">Saldo</th>
                                    <th className="text-left px-4 py-2.5">Zugeordnet</th>
                                    <th className="text-left px-4 py-2.5">Vermerk</th>
                                    <th className="px-4 py-2.5" />
                                </tr>
                            </thead>
                            <tbody>
                            {filtered.map((k) => (
                                <tr key={k.iban} className="border-t border-gray-100 hover:bg-gray-50/60">
                                    <td className="px-4 py-2.5">
                                        <div className="font-medium text-slate-900">{k.bezeichnung}</div>
                                        <div className="text-[11px] text-gray-400 tabular-nums">{k.iban}</div>
                                    </td>
                                    <td className="px-4 py-2.5">
                                        {gesperrt ? (
                                            <Merkmal text={ART[k.art]?.label || k.art}
                                                     ton={ART[k.art]?.ton || "text-gray-600 bg-gray-50 border-gray-200"} />
                                        ) : (
                                            <Select value={k.art} onValueChange={(v) => artAendern(k, v)}>
                                                <SelectTrigger className="w-[132px] h-8"
                                                               data-testid={`art-${k.iban}`}>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {Object.entries(ART).map(([w, v]) => (
                                                        <SelectItem key={w} value={w}>{v.label}</SelectItem>
                                                    ))}
                                                    {k.art_ist_handkorrektur && (
                                                        <SelectItem value="__erkennung">
                                                            ↺ Erkennung ({ART[k.art_erkannt]?.label || k.art_erkannt})
                                                        </SelectItem>
                                                    )}
                                                </SelectContent>
                                            </Select>
                                        )}
                                        {k.art_ist_handkorrektur && (
                                            <div className="mt-1 text-[10px] text-gray-400">
                                                <Grund text={k.art_manuell_grund}>
                                                    von Hand · erkannt: {ART[k.art_erkannt]?.label || k.art_erkannt}
                                                </Grund>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-2.5 text-right tabular-nums">{euro(k.saldo)}</td>
                                    <td className="px-4 py-2.5 text-gray-600">
                                        {k.zuordnung?.bezeichnung || (
                                            k.art === "miete"
                                                ? <span className="text-rose-600 inline-flex items-center gap-1">
                                                      <AlertCircle size={12} /> fehlt
                                                  </span>
                                                : <span className="text-gray-400">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2.5">
                                        {k.abschluss ? (
                                            <Grund text={k.abschluss.grund}>
                                                <Merkmal
                                                    text={k.abschluss.status === "rueckbehalt"
                                                        ? `Rückbehalt: ${k.abschluss.anlass}`
                                                        : "abgeschlossen"}
                                                    ton={k.abschluss.status === "rueckbehalt"
                                                        ? "text-amber-700 bg-amber-50 border-amber-200"
                                                        : "text-gray-600 bg-gray-50 border-gray-200"} />
                                            </Grund>
                                        ) : <span className="text-gray-300">—</span>}
                                    </td>
                                    <td className="px-4 py-2.5 text-right">
                                        <Button variant="ghost" size="sm" className="h-7 px-2"
                                                onClick={() => setAbschlussKonto(k)}
                                                disabled={gesperrt} grund={grund}
                                                data-testid={`vermerk-${k.iban}`}>
                                            <ShieldCheck size={14} />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-400">
                                    Keine Konten. Über „Auszug einlesen" einen Banking4-Export laden.
                                </td></tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="kautionen" className="mt-4 space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <SearchBox value={kq} onChange={setKq} testid="kautionen-suche"
                                   placeholder="Konto, Mieter, Einheit …" />
                        <MatchCount matches={kFiltered.length} total={kautionsListe.length} label="Kautionen" />
                        <div className="ml-auto">
                            <Button size="sm" className="bg-slate-900 hover:bg-slate-800 h-9"
                                    onClick={() => setVorschlagArt("kaution")} data-testid="btn-kautionen-zuordnen"
                                    disabled={gesperrt} grund={grund}>
                                <Link2 size={14} className="mr-1.5" /> Kautionen zuordnen
                            </Button>
                        </div>
                    </div>

                    {kautionen?.summe_je_zustand && (
                        <div className="flex gap-2 flex-wrap text-xs">
                            {Object.entries(kautionen.summe_je_zustand).map(([z, betrag]) => (
                                <div key={z} className="px-3 py-1.5 rounded border border-gray-200 bg-white">
                                    <span className="text-gray-500">{ZUSTAND[z]?.label || z}</span>
                                    <b className="ml-2 tabular-nums">{euro(betrag)}</b>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* overflow-x-auto, nicht overflow-hidden: die Tabelle ist breiter als der
                        Bildschirm, sobald genug Spalten da sind — mit "hidden" verschwinden die
                        rechten Spalten lautlos und sind auf keinem Weg erreichbar. Gemessen am
                        10.09.2026 bei 1280 px: Mieter 210 px abgeschnitten, Einheiten 183 px. */}
                    <div className="bg-white border border-gray-200 rounded-md overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                                <tr>
                                    <th className="text-left px-4 py-2.5">Konto</th>
                                    <th className="text-left px-4 py-2.5">Einheit</th>
                                    <th className="text-left px-4 py-2.5">Mieter</th>
                                    <th className="text-right px-4 py-2.5">Vereinbart</th>
                                    <th className="text-right px-4 py-2.5">Auf der Bank</th>
                                    <th className="text-right px-4 py-2.5">Differenz</th>
                                    <th className="text-left px-4 py-2.5">Zustand</th>
                                </tr>
                            </thead>
                            <tbody>
                            {kFiltered.map((x) => (
                                <tr key={x.id} className="border-t border-gray-100 hover:bg-gray-50/60">
                                    <td className="px-4 py-2.5 font-medium text-slate-900">{x.account_bezeichnung}</td>
                                    <td className="px-4 py-2.5 text-gray-600">{x.einheit || <span className="text-gray-300">—</span>}</td>
                                    <td className="px-4 py-2.5 text-gray-600">{x.mieter || <span className="text-gray-300">—</span>}</td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-500">
                                        {x.vereinbart == null ? "—" : euro(x.vereinbart)}
                                    </td>
                                    <td className="px-4 py-2.5 text-right tabular-nums">{euro(x.saldo)}</td>
                                    <td className={`px-4 py-2.5 text-right tabular-nums ${
                                        x.differenz == null ? "text-gray-300"
                                            : Math.abs(x.differenz) < 0.01 ? "text-gray-400"
                                            : x.differenz < 0 ? "text-rose-600" : "text-emerald-600"}`}>
                                        {x.differenz == null ? "—" : euro(x.differenz)}
                                    </td>
                                    <td className="px-4 py-2.5">
                                        <Grund text={x.hinweis}>
                                            <Merkmal text={ZUSTAND[x.zustand]?.label || x.zustand}
                                                     ton={ZUSTAND[x.zustand]?.ton || "text-gray-600 bg-gray-50 border-gray-200"} />
                                        </Grund>
                                    </td>
                                </tr>
                            ))}
                            {kFiltered.length === 0 && (
                                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-400">
                                    Keine Kautionen erfasst. Über „Kautionen zuordnen" aus den
                                    eingelesenen Konten ableiten.
                                </td></tr>
                            )}
                            </tbody>
                        </table>
                    </div>

                    <div className="text-xs text-gray-400 leading-relaxed max-w-3xl">
                        „Vereinbart" kommt aus dem Mieterdatensatz, „auf der Bank" vom Kontoauszug.
                        Die Differenz ist die eigentliche Auskunft: sie zeigt, wo die vereinbarte
                        Kaution und das tatsächlich gehaltene Geld auseinanderlaufen. Ist nichts
                        vereinbart hinterlegt, steht dort ein Strich — nicht Null.
                    </div>
                </TabsContent>
            </Tabs>

            <ImportDialog open={importOffen} onOpenChange={setImportOffen} onDone={load} />
            <VorschlagDialog art={vorschlagArt} open={!!vorschlagArt}
                             onOpenChange={(o) => !o && setVorschlagArt(null)} onDone={load} />
            <AbschlussDialog konto={abschlussKonto} open={!!abschlussKonto}
                             onOpenChange={(o) => !o && setAbschlussKonto(null)} onSaved={load} />
        </div>
    );
};

export default Konten;
